/**
 * compliance-reports.ts — Chunk 185: Institutional Compliance Reporting Suite
 * Typed API client for compliance report schedules and runs.
 */
import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReportType = 'full_portfolio' | 'trade_history' | 'risk_summary' | 'defi_exposure' | 'tax_summary';
export type ReportFrequency = 'weekly' | 'monthly' | 'quarterly';
export type ReportFormat = 'pdf' | 'csv';
export type RunStatus = 'pending' | 'generating' | 'complete' | 'failed';

export interface ComplianceSchedule {
  id: string;
  user_id: string;
  report_type: ReportType;
  frequency: ReportFrequency;
  format: ReportFormat;
  recipients: string[];
  last_run_at: string | null;
  next_run_at: string;
  is_active: boolean;
  created_at: string;
}

export interface ComplianceRun {
  id: string;
  schedule_id: string;
  report_type: ReportType;
  period_start: string;
  period_end: string;
  format: ReportFormat;
  file_url: string | null;
  status: RunStatus;
  generated_at: string | null;
  created_at: string;
}

export interface CreateSchedulePayload {
  report_type: ReportType;
  frequency: ReportFrequency;
  format: ReportFormat;
  recipients: string[];
}

export interface GenerateReportPayload {
  schedule_id: string;
  report_type: ReportType;
  format: ReportFormat;
  period_start: string;
  period_end: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export async function listComplianceSchedules(userId: string): Promise<{ schedules: ComplianceSchedule[] }> {
  const res = await apiClient.get<{ schedules: ComplianceSchedule[] }>(
    `/analytics/compliance/schedules/${encodeURIComponent(userId)}`,
  );
  return res.data;
}

export async function createComplianceSchedule(
  userId: string,
  payload: CreateSchedulePayload,
): Promise<{ schedule: ComplianceSchedule }> {
  const res = await apiClient.post<{ schedule: ComplianceSchedule }>(
    `/analytics/compliance/schedules/${encodeURIComponent(userId)}`,
    payload,
  );
  return res.data;
}

export async function deleteComplianceSchedule(userId: string, id: string): Promise<{ deleted: boolean; id: string }> {
  const res = await apiClient.delete<{ deleted: boolean; id: string }>(
    `/analytics/compliance/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`,
  );
  return res.data;
}

export async function listComplianceRuns(userId: string): Promise<{ runs: ComplianceRun[] }> {
  const res = await apiClient.get<{ runs: ComplianceRun[] }>(
    `/analytics/compliance/runs/${encodeURIComponent(userId)}`,
  );
  return res.data;
}

export async function generateComplianceReport(
  userId: string,
  payload: GenerateReportPayload,
): Promise<{ run: ComplianceRun }> {
  const res = await apiClient.post<{ run: ComplianceRun }>(
    `/analytics/compliance/generate/${encodeURIComponent(userId)}`,
    payload,
  );
  return res.data;
}

// ─── Label helpers ────────────────────────────────────────────────────────────

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  full_portfolio: 'Full Portfolio',
  trade_history: 'Trade History',
  risk_summary: 'Risk Summary',
  defi_exposure: 'DeFi Exposure',
  tax_summary: 'Tax Summary',
};

export const FREQUENCY_LABELS: Record<ReportFrequency, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

export const FORMAT_LABELS: Record<ReportFormat, string> = {
  pdf: 'PDF',
  csv: 'CSV',
};
